import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../AuthContext';
import { deleteProject, fetchCreditsOverview } from '../../services/credits';
import { formatCredits, formatNumber } from './creditFormula';

const STATUS_COLORS = {
    requested: 'default',
    active: 'primary',
    completed: 'success',
    cancelled: 'error',
};

function ProjectDetailDialog({ project, onClose, onDeleted }) {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    if (!project) return null;
    const remainingCap = Math.max(0, project.review_cap - project.posts_found);
    const isUnused =
        project.posts_found === 0
        && project.searches_made === 0
        && project.custom_assessments_run === 0;

    const doDelete = async () => {
        setDeleteError(null);
        setDeleting(true);
        try {
            await deleteProject(project.project_id);
            onDeleted(project.project_id);
        } catch (e) {
            setDeleteError(e.message);
            setDeleting(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{project.name}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1.5, columnGap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip size="small" label={project.status} color={STATUS_COLORS[project.status] || 'default'} sx={{ justifySelf: 'start' }} />

                    <Typography variant="body2" color="text.secondary">Review cap</Typography>
                    <Typography variant="body2">{formatNumber(project.review_cap)}</Typography>

                    <Typography variant="body2" color="text.secondary">Posts found</Typography>
                    <Typography variant="body2">{formatNumber(project.posts_found)} ({formatNumber(remainingCap)} under cap)</Typography>

                    <Typography variant="body2" color="text.secondary">HN searches made</Typography>
                    <Typography variant="body2">{formatNumber(project.searches_made)}</Typography>

                    <Typography variant="body2" color="text.secondary">Custom assessments run</Typography>
                    <Typography variant="body2">{formatNumber(project.custom_assessments_run)}</Typography>

                    <Typography variant="body2" color="text.secondary">Quoted credits</Typography>
                    <Typography variant="body2">{formatCredits(project.quoted_credits)}</Typography>

                    <Typography variant="body2" color="text.secondary">Credits charged</Typography>
                    <Typography variant="body2"><strong>{formatCredits(project.credits_used)}</strong></Typography>

                    <Typography variant="body2" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{new Date(project.created_at).toLocaleDateString()}</Typography>

                    {project.usage_updated_at && (
                        <>
                            <Typography variant="body2" color="text.secondary">Usage last updated</Typography>
                            <Typography variant="body2">{new Date(project.usage_updated_at).toLocaleString()}</Typography>
                        </>
                    )}
                </Box>

                {confirmingDelete && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Permanently delete this project? Per-topic config will be removed.
                            This can't be undone.
                        </Typography>
                        {deleteError && (
                            <Typography variant="body2" color="error.dark" sx={{ mb: 1 }}>
                                {deleteError}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button size="small" color="error" variant="contained" onClick={doDelete} disabled={deleting}>
                                {deleting ? 'Deleting…' : 'Confirm delete'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {isUnused && !confirmingDelete && (
                    <Button color="error" onClick={() => setConfirmingDelete(true)}>
                        Delete project
                    </Button>
                )}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function CreditsOverview() {
    const { session } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [contract, setContract] = useState(null);
    const [projects, setProjects] = useState([]);
    const [totals, setTotals] = useState({ used: 0, total: 0, remaining: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const handleRowDelete = async (project, e) => {
        e.stopPropagation();
        const ok = window.confirm(`Delete "${project.name}"? This can't be undone.`);
        if (!ok) return;
        setError(null);
        setDeletingId(project.project_id);
        try {
            await deleteProject(project.project_id);
            setProjects(prev => prev.filter(p => p.project_id !== project.project_id));
        } catch (err) {
            setError(err.message);
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (!session) return;
        let cancelled = false;
        setLoading(true);
        fetchCreditsOverview()
            .then(data => {
                if (cancelled) return;
                setContract(data.contract);
                setProjects(data.projects || []);
                setTotals(data.totals || { used: 0, total: 0, remaining: 0 });
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                setError(err.message);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [session]);

    if (!session) {
        return <Typography>Sign in to view credits.</Typography>;
    }
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    const goToNewProject = () => {
        navigate('/projects/new', { state: { from: location.pathname + location.search } });
    };

    const pct = totals.total > 0 ? Math.min(100, (totals.used / totals.total) * 100) : 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Credits</Typography>
                <Button variant="contained" onClick={goToNewProject}>
                    New project request
                </Button>
            </Box>

            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

            <Paper sx={{ p: 3, mb: 3 }}>
                {contract ? (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">
                                <strong>{formatCredits(totals.used)}</strong> of {formatCredits(totals.total)} credits used
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {formatCredits(totals.remaining)} remaining
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Contract period: {new Date(contract.period_start).toLocaleDateString()} – {new Date(contract.period_end).toLocaleDateString()}
                        </Typography>
                    </>
                ) : (
                    <Typography color="text.secondary">No active contract on file for this organization.</Typography>
                )}
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Project</TableCell>
                            <TableCell align="right">Cap</TableCell>
                            <TableCell align="right">Posts found</TableCell>
                            <TableCell align="right">Searches</TableCell>
                            <TableCell align="right">Custom runs</TableCell>
                            <TableCell align="right">Credits</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {projects.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography color="text.secondary" sx={{ py: 2 }}>
                                        No projects yet. Click "New project request" to start one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {projects.map(p => (
                            <TableRow
                                key={p.project_id}
                                hover
                                onClick={() => setSelected(p)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{p.name}</TableCell>
                                <TableCell align="right">{formatNumber(p.review_cap)}</TableCell>
                                <TableCell align="right">{formatNumber(p.posts_found)}</TableCell>
                                <TableCell align="right">{formatNumber(p.searches_made)}</TableCell>
                                <TableCell align="right">{formatNumber(p.custom_assessments_run)}</TableCell>
                                <TableCell align="right">
                                    <strong>{formatCredits(p.credits_used)}</strong>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                        / {formatCredits(p.quoted_credits)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={p.status} color={STATUS_COLORS[p.status] || 'default'} />
                                </TableCell>
                                <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                                    {p.posts_found === 0 && p.searches_made === 0 && p.custom_assessments_run === 0 ? (
                                        <Tooltip title="Delete project">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleRowDelete(p, e)}
                                                    disabled={deletingId === p.project_id}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <ProjectDetailDialog
                project={selected}
                onClose={() => setSelected(null)}
                onDeleted={(deletedId) => {
                    setProjects(prev => prev.filter(p => p.project_id !== deletedId));
                    setSelected(null);
                }}
            />
        </Box>
    );
}

export default CreditsOverview;
